import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import AnimatedSection from '../Animation/AnimatedSection';

const Section = styled.section`
  padding: 6rem 0;
  background: linear-gradient(180deg, ${props => props.theme.colors.grayLight} 0%, ${props => props.theme.colors.white} 100%);
`;

const SectionTitle = styled(motion.h2)`
  color: ${props => props.theme.colors.primary};
  text-align: center;
  position: relative;
  padding-bottom: 1rem;
  margin-bottom: 1.25rem;
  text-transform: uppercase;
  font-style: italic;
  
  &:after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background-color: ${props => props.theme.colors.grayMedium};
  }
`;

const Intro = styled(motion.p)`
  max-width: 820px;
  margin: 0.5rem auto 2.5rem auto;
  text-align: center;
  font-size: ${props => props.theme.fontSizes.bodyLg};
  line-height: ${props => props.theme.lineHeights.relaxed};
`;

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2rem;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

const Card = styled(motion.article)`
  flex: 0 1 300px;
  max-width: 320px;
  background-color: ${props =>
    props.$variant === 'primary'
      ? props.theme.colors.primary
      : props.$variant === 'light'
      ? props.theme.colors.grayLight
      : props.theme.colors.white};
  color: ${props =>
    props.$variant === 'primary'
      ? props.theme.colors.white
      : props.theme.colors.black};
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: ${props => props.theme.boxShadow};
  overflow: hidden; /* evita que a imagem ultrapasse o card */
  text-align: left;
  display: flex;
  flex-direction: column;
`;

const Avatar = styled.div`
  width: 100%;
  aspect-ratio: 4 / 5; /* proporção retrato */
  overflow: hidden;
  border-radius: ${props => props.theme.borderRadius.medium} ${props => props.theme.borderRadius.medium} 0 0;
  background: none;
  margin-bottom: 1rem;
`;

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border: none;
`;

const Initials = styled.span`
  font-family: ${props => props.theme.fonts.heading};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.theme.colors.primary};
  font-size: 2rem;
`;

const Name = styled.h3`
  margin: 1rem 1rem 0.25rem 1rem;
  color: ${props => props.$variant === 'primary' ? props.theme.colors.white : props.theme.colors.primary};
  font-weight: 700;
  text-transform: uppercase;
`;

const Role = styled.p`
  margin: 0 1rem 0.75rem 1rem;
  color: ${props => props.$variant === 'primary' ? 'rgba(255,255,255,0.85)' : props.theme.colors.grayMedium};
  font-style: italic;
  font-family: ${props => props.theme.fonts.subheading};
`;

const Bio = styled.p`
  margin: 0 1rem 1.5rem 1rem;
  text-align: justify;
  color: ${props => props.$variant === 'primary' ? 'rgba(255,255,255,0.95)' : 'inherit'};
  font-size: 0.95rem;
`;

const HoverLine = styled.div`
  width: 0;
  height: 3px;
  background: ${props => props.theme.colors.primary};
  margin-top: 1rem;
`;

const PersonCard = ({ person, variant }) => {
  const [imgError, setImgError] = useState(false);
  const initials = person?.name
    ? person.name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(s => s[0])
        .join('')
        .toUpperCase()
    : 'LM';

  return (
    <Card
      $variant={variant}
      whileHover={{ y: -8, boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12)' }}
      transition={{ type: 'spring', stiffness: 300 }}
    >
      <motion.div whileHover={{ scale: 1.03 }}>
        <Avatar aria-label={`Foto de ${person.name}`} $variant={variant}>
          {!imgError && person.image ? (
            <AvatarImg
              src={person.image}
              alt={`Foto de ${person.name}`}
              onError={() => setImgError(true)}
            />
          ) : (
            <Initials>{initials}</Initials>
          )}
        </Avatar>
      </motion.div>
      <Name $variant={variant}>{person.name}</Name>
      {person.role && <Role $variant={variant}>{person.role}</Role>}
      <Bio $variant={variant}>{person.bio}</Bio>
      <HoverLine as={motion.div} whileHover={{ width: '60px' }} />
    </Card>
  );
};

const Professionals = () => {
  const team = [
    {
      name: 'Lilian Costa',
      role: 'Chief Executive Officer',
      image: '/assets/team/lilian.png',
      bio: 'Engenheira de Produção com experiência internacional após dois anos nos EUA. Lidera operações com foco em eficiência, excelência e gestão estratégica, garantindo entregas precisas e soluções alinhadas às necessidades de cada cliente.'
    },
    // {
    //   name: 'Mateus Oliveira',
    //   role: 'Chief Executive Officer',
    //   image: '/assets/team/mateus.png',
    //   bio: 'Todavia, o desafiador cenário globalizado é uma das consequências do processo de comunicação como um todo.'
    // },
  ];

  return (
    <Section id="profissionais">
      <div className="container">
        <SectionTitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6 }}
        >
          Quem Faz a LM Consulting Acontecer
        </SectionTitle>

        <Intro
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          Conheça a profissional que, com expertise técnica e atendimento próximo, impulsiona
          os projetos e resultados ao lado dos clientes.
        </Intro>

        <Grid>
          {team.map((person, i) => {
            const variant = person.color ? person.color : (i % 2 === 0 ? 'primary' : 'light');
            return (
              <AnimatedSection key={i} animation={i % 2 === 0 ? 'fadeInLeft' : 'fadeInRight'} delay={0.05 * i}>
                <PersonCard person={person} variant={variant} />
              </AnimatedSection>
            );
          })}
        </Grid>
      </div>
    </Section>
  );
};

export default Professionals;
